/**
 * MediaPreview Component
 *
 * Displays a media item preview (image or video) with menu options.
 * Used in the upload media section to show uploaded content.
 *
 * Layout modes:
 * - "column" (default): Square grid card for profiles/rituals/sessions
 * - "row": Horizontal row card for communities (Figma: 810×80, thumbnail 90×60)
 */

import { GripHorizontal, MoreVertical, Music } from "lucide-react"
import { useState } from "react"
import {
  ImagePlaceholderIcon,
  ImageSquareIcon,
  PencilEditIcon,
  PlayTriangleIcon,
  ReOrderDotsIcon,
  TrashDeleteIcon,
  VideoCameraIcon,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib"

interface MediaPreviewProps {
  type: "image" | "video" | "audio"
  src?: string
  thumbnailSrc?: string
  title?: string
  url?: string
  subtitle?: string
  isCover?: boolean
  isAiGenerated?: boolean
  isDragging?: boolean
  variant?: "default" | "editProfile"
  layout?: "row" | "column"
  dragHandleProps?: Record<string, unknown>
  // Inline title editing for video rows
  editingTitle?: string
  onEditingTitleChange?: (value: string) => void
  onEditTitleSubmit?: () => void
  onEditTitleCancel?: () => void
  onCoverSelect?: () => void
  onRemove?: () => void
  onChange?: () => void
  onRegenerate?: () => void
  onEditImage?: () => void
  onEditThumbnail?: () => void
  coverImageUrl?: string
}

export function MediaPreview({
  type,
  src,
  thumbnailSrc,
  title,
  subtitle,
  isCover = false,
  isAiGenerated = false,
  isDragging = false,
  variant = "default",
  layout = "column",
  dragHandleProps,
  editingTitle,
  onEditingTitleChange,
  onEditTitleSubmit,
  onEditTitleCancel,
  onCoverSelect,
  onRemove,
  onChange,
  onRegenerate,
  onEditImage,
  onEditThumbnail,
  coverImageUrl,
}: MediaPreviewProps) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true)
  }

  // ---- Row layout (community) ---- Figma: 810×80, bg #f2f0eb, stroke #f2f0eb, r15
  if (layout === "row") {
    const displayTitle =
      title || (type === "image" ? "Image" : type === "audio" ? "Audio" : "Video")
    const displaySubtitle =
      subtitle || (type === "video" ? "MP4" : type === "audio" ? "Audio" : "JPG")

    return (
      <>
        {/* Video playback modal */}
        {type === "video" && (
          <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
            <DialogContent className="max-w-3xl w-full p-0 bg-black/90 border-none">
              <video
                src={src}
                className="w-full h-auto max-h-[80vh] object-contain"
                controls
                autoPlay
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Audio playback modal */}
        {type === "audio" && src && (
          <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
            <DialogContent className="max-w-3xl w-full p-0 bg-black border-none overflow-hidden">
              <div className="relative w-full aspect-video">
                {/* Background: thumbnail if available, else cover image, else gradient */}
                {thumbnailSrc || coverImageUrl ? (
                  <img
                    src={thumbnailSrc || coverImageUrl}
                    alt={displayTitle}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-dark to-brand-slate" />
                )}
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-4">
                  <p className="text-white text-lg font-semibold">{displayTitle}</p>
                  <audio src={src} controls autoPlay className="w-3/4" />
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        <div
          className={cn(
            "flex items-center h-20 rounded-[15px] border border-card bg-card/30 px-6 py-5 gap-4",
            isDragging && "opacity-50",
          )}
        >
          {/* Drag handle — Figma: Re Order Dots 20×20 */}
          {dragHandleProps && (
            <div className="flex-shrink-0 cursor-grab active:cursor-grabbing" {...dragHandleProps}>
              <ReOrderDotsIcon size={13} className="text-primary" />
            </div>
          )}

          {/* Thumbnail — Figma: 90×60, r10, bg #020202 */}
          <div className="w-[90px] h-[60px] rounded-[10px] overflow-hidden flex-shrink-0 bg-brand-dark relative">
            {type === "image" && src ? (
              <>
                <div className="absolute inset-0 flex items-center justify-center cursor-pointer z-5">
                  <ImageSquareIcon size={15} className="text-white" />
                </div>
                <img src={src} alt={displayTitle} className="w-full h-full object-cover" />
              </>
            ) : type === "video" && (thumbnailSrc || src) ? (
              <>
                {thumbnailSrc ? (
                  <img
                    src={thumbnailSrc}
                    alt={displayTitle}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    src={src}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                )}
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer z-5"
                  onClick={handleOpenVideoModal}
                >
                  <PlayTriangleIcon size={15} className="text-white" />
                </div>
              </>
            ) : type === "audio" && (thumbnailSrc || coverImageUrl) ? (
              <>
                <img
                  src={thumbnailSrc || coverImageUrl}
                  alt={displayTitle}
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer z-5"
                  onClick={handleOpenVideoModal}
                >
                  <Music size={17} className="text-white" />
                </div>
              </>
            ) : type === "audio" ? (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={handleOpenVideoModal}
              >
                <Music size={17} className="text-brand-offwhite" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {type === "video" ? (
                  <PlayTriangleIcon size={17} className="text-brand-offwhite" />
                ) : (
                  <ImageSquareIcon size={15} className="text-brand-offwhite" />
                )}
              </div>
            )}
          </div>

          {/* Text section — Figma: title 14px Montserrat w600, subtitle 12px Montserrat w400 */}
          <div className="flex flex-col justify-center min-w-0 flex-1">
            {editingTitle !== undefined && onEditingTitleChange ? (
              <input
                type="text"
                value={editingTitle}
                onChange={e => onEditingTitleChange(e.target.value)}
                onBlur={onEditTitleSubmit}
                onKeyDown={e => {
                  if (e.key === "Enter") onEditTitleSubmit?.()
                  if (e.key === "Escape") onEditTitleCancel?.()
                }}
                placeholder="Enter video title"
                autoFocus
                className="text-[14px] font-semibold text-primary bg-transparent border focus:outline-none focus:ring-0 w-full font-montserrat p-3.75 rounded-[10px]"
              />
            ) : (
              <>
                <p className="text-[14px] font-semibold text-primary font-montserrat truncate">
                  {displayTitle}
                </p>
                <p className="text-[12px] font-normal text-primary/60 font-montserrat truncate">
                  {displaySubtitle}
                </p>
              </>
            )}
          </div>

          {/* Right-side actions — Figma icons 20×20, color #f2f0eb (over dark bg) */}
          <div className="flex items-center gap-3.5 flex-shrink-0">
            {/* IMAGE: Cover badge — always visible */}
            {type === "image" && isCover && (
              <span className="text-[13px] font-normal text-body font-montserrat">Cover</span>
            )}

            {/* Editing mode: Save/Cancel only (all screens) */}
            {(type === "video" || type === "audio") &&
              editingTitle !== undefined &&
              onEditTitleSubmit && (
                <button
                  type="button"
                  onClick={onEditTitleSubmit}
                  className="text-[12px] font-semibold text-primary font-montserrat cursor-pointer"
                >
                  Save
                </button>
              )}

            {/* Non-editing mode */}
            {editingTitle === undefined && (
              <>
                {/* Desktop inline actions */}
                <div className="hidden md:flex items-center gap-3.5">
                  {type === "image" && !isCover && onCoverSelect && (
                    <button
                      type="button"
                      onClick={onCoverSelect}
                      className="text-[13px] font-semibold text-primary font-montserrat cursor-pointer"
                    >
                      Set as cover
                    </button>
                  )}
                  {(type === "video" || type === "audio") && onChange && (
                    <button
                      type="button"
                      onClick={onChange}
                      className="p-0 text-primary hover:text-primary/80 cursor-pointer"
                      aria-label={type === "audio" ? "Edit audio title" : "Edit video title"}
                    >
                      <PencilEditIcon size={16} />
                    </button>
                  )}
                  {(type === "video" || type === "audio") && onEditThumbnail && (
                    <button
                      type="button"
                      onClick={onEditThumbnail}
                      className="p-0 text-primary hover:text-primary/80 cursor-pointer"
                      aria-label="Upload thumbnail"
                    >
                      <ImageSquareIcon size={15} />
                    </button>
                  )}
                  {type === "image" && onEditImage && (
                    <button
                      type="button"
                      onClick={onEditImage}
                      className="p-0 text-primary hover:text-primary/80 cursor-pointer"
                      aria-label="Edit image"
                    >
                      <ImageSquareIcon size={15} />
                    </button>
                  )}
                  {onRemove && (
                    <button
                      type="button"
                      onClick={onRemove}
                      className="p-0 text-primary hover:text-primary/80 cursor-pointer"
                      aria-label="Delete"
                    >
                      <TrashDeleteIcon size={16} />
                    </button>
                  )}
                </div>

                {/* Mobile three-dot dropdown */}
                <div className="flex md:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="p-0 text-primary hover:text-primary/80 cursor-pointer"
                        aria-label="More options"
                      >
                        <MoreVertical size={18} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {type === "image" && !isCover && onCoverSelect && (
                        <DropdownMenuItem onClick={onCoverSelect} className="cursor-pointer">
                          Set as cover
                        </DropdownMenuItem>
                      )}
                      {(type === "video" || type === "audio") && onChange && (
                        <DropdownMenuItem onClick={onChange} className="cursor-pointer">
                          {type === "audio" ? "Edit audio title" : "Edit video title"}
                        </DropdownMenuItem>
                      )}
                      {(type === "video" || type === "audio") && onEditThumbnail && (
                        <DropdownMenuItem onClick={onEditThumbnail} className="cursor-pointer">
                          Upload thumbnail
                        </DropdownMenuItem>
                      )}
                      {type === "image" && onEditImage && (
                        <DropdownMenuItem onClick={onEditImage} className="cursor-pointer">
                          Edit image
                        </DropdownMenuItem>
                      )}
                      {onRemove && (
                        <DropdownMenuItem
                          onClick={onRemove}
                          variant="destructive"
                          className="cursor-pointer"
                        >
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  // ---- Column layout — video (non-row) ----
  if (type === "video") {
    return (
      <>
        <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
          <DialogContent className="max-w-3xl w-full p-0 bg-black/90 border-none">
            <video
              src={src}
              className="w-full h-auto max-h-[80vh] object-contain"
              controls
              autoPlay
            />
          </DialogContent>
        </Dialog>
        <div className="flex items-center gap-4 rounded-lgx relative group">
          {/* Video preview using HTML5 video element */}
          <div className="w-50 h-30 bg-themed rounded flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {src ? (
              <video src={src} className="w-full h-full object-cover" muted preload="metadata" />
            ) : (
              <VideoCameraIcon className="text-brown-primary" />
            )}
            {/* Play icon overlay when we have a valid source - opens modal */}
            {src && (
              <>
                <div
                  className="absolute inset-0 flex items-center justify-center z-5 cursor-pointer"
                  onClick={handleOpenVideoModal}
                >
                  <div className="w-8 h-8 bg-transparent border-2 border-white rounded-full flex items-center justify-center">
                    <PlayTriangleIcon className="text-brand-dark" size={10} color={"#ffffff"} />
                  </div>
                </div>
                <div className="absolute inset-0 bg-[rgb(0,0,0,0.5)] z-2" />
              </>
            )}
          </div>

          {/* Edit and Delete icons for video */}
          {src && (
            <div className="flex items-center gap-0">
              {onChange && (
                <button
                  type="button"
                  onClick={onChange}
                  className="p-2 rounded-full bg-transparent text-white transition-colors cursor-pointer"
                  aria-label="Edit video"
                >
                  <PencilEditIcon size={16} />
                </button>
              )}
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="p-2 rounded-full bg-transparent text-white transition-colors cursor-pointer"
                  aria-label="Delete video"
                >
                  <TrashDeleteIcon size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </>
    )
  }

  // ---- Column layout — image (default) ----
  return (
    <div
      className={cn(
        "relative aspect-4/3 md:w-35 md:h-35 rounded-lg bg-themed overflow-hidden group",
        isDragging && "opacity-50",
      )}
    >
      {/* Top row - visible on hover */}
      <div className="absolute top-0 left-1 right-0 p-1 pt-2 flex items-center justify-between z-50 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Left: Grip (drag handle when sortable) */}
        <GripHorizontal
          className={cn(
            "w-5 h-5 text-white",
            dragHandleProps ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          )}
          {...(dragHandleProps ?? {})}
        />

        {/* Right: Edit + Remove icons */}
        <div className="flex items-center gap-0">
          {onEditImage && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onEditImage()
              }}
              className="p-1 rounded-full cursor-pointer"
            >
              <PencilEditIcon size={16} />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onRemove()
              }}
              className="p-1 rounded-full cursor-pointer"
            >
              <TrashDeleteIcon size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Center overlay - visible on hover */}
      <div className="absolute pb-2 inset-0 bg-[rgb(0,0,0,0.6)] opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0 z-40 rounded-lg">
        {type === "image" && onCoverSelect && !isCover && variant !== "editProfile" && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onCoverSelect?.()
            }}
            className="text-[rgb(0,0,0)] bg-white text-[13px] px-3 py-0"
          >
            Set as cover
          </Button>
        )}
        {isAiGenerated && onRegenerate && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onRegenerate?.()
            }}
            className="text-[rgb(0,0,0)] bg-white"
          >
            Regenerate
          </Button>
        )}
      </div>

      {/* Image or placeholder */}
      {src ? (
        <div className="h-full w-full">
          <img src={src} alt="Uploaded" className="w-full h-full object-cover rounded-lg" />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImagePlaceholderIcon className="text-brown-primary" />
        </div>
      )}

      {/* Cover badge - shown when image is cover */}
      {isCover && (
        <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-[rgb(0,0,0,0.7)] z-30">
          <p className="text-[13px] font-medium text-white text-center">Cover</p>
        </div>
      )}
    </div>
  )
}
